/**
 * Phase 195.d — Honors Snapshot Share (OpenGraph PNG Generator)
 *
 * Renders an off-screen 1200×630 canvas of the academy's honors hub →
 * captures it via html2canvas → exports as PNG.
 *
 *   • Mobile (Web Share API supported): native sheet → user taps Instagram/
 *     WhatsApp/Stories/Email directly with the PNG attached.
 *   • Desktop / unsupported: falls back to a direct PNG download.
 *
 * The blueprint container is absolute-positioned off-screen (left:-9999px)
 * so it never affects layout but renders at exact 1200×630 with 2× DPI
 * scaling for retina-crisp output.
 *
 * Renders nothing if the academy has zero trophies AND zero progressions
 * — there's nothing to brag about yet.
 */
import { useRef, useState } from 'react';
import { Share2, Download, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const NETWORKS = [
  { label: 'Native share', test: () => typeof navigator !== 'undefined' && !!navigator.share },
];

function slugify(s) {
  return (s || 'academy')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

async function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.95));
}

export default function HonorsSnapshotShare({ academy, stats, trophies = [], progressions = [] }) {
  const blueprintRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Nothing to share — render nothing.
  if (trophies.length === 0 && progressions.length === 0) return null;

  const top3 = trophies.slice(0, 3);
  const academyName = academy?.name || 'Academy';
  const fileName = `${slugify(academyName)}-honors-${new Date().toISOString().slice(0, 10)}.png`;

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    setDone(false);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const node = blueprintRef.current;
      if (!node) throw new Error('Snapshot canvas blueprint not mounted');

      // Force-show the off-screen node only during capture to ensure all
      // children paint. Move it back after.
      const originalLeft = node.style.left;
      node.style.left = '0';
      node.style.top = '-9999px'; // Still hidden, but rendered above viewport

      const canvas = await html2canvas(node, {
        width: 1200,
        height: 630,
        scale: 2,
        backgroundColor: '#020617',
        useCORS: true,
        logging: false,
        windowWidth: 1200,
        windowHeight: 630,
      });

      node.style.left = originalLeft;

      const blob = await canvasToBlob(canvas);
      if (!blob) throw new Error('PNG encoding failed');

      // Web Share API (mobile) → native sheet with PNG attached
      const file = new File([blob], fileName, { type: 'image/png' });
      const sharePayload = {
        title: `${academyName} · Honors Snapshot`,
        text: `Look what ${academyName} is building — official Out-Play Honors Snapshot.`,
        files: [file],
      };
      if (
        typeof navigator !== 'undefined' &&
        navigator.canShare && navigator.canShare(sharePayload) &&
        navigator.share
      ) {
        try {
          await navigator.share(sharePayload);
          setDone(true);
          toast.success('Shared!');
          return;
        } catch (e) {
          // User cancelled — fall through to download
          if (e?.name !== 'AbortError') {
            console.warn('Web Share failed, falling back to download', e);
          } else {
            setBusy(false);
            return;
          }
        }
      }

      // Desktop / unsupported — straight PNG download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      setDone(true);
      toast.success('Snapshot downloaded');
    } catch (err) {
      console.error('Snapshot generation failed:', err);
      toast.error('Snapshot failed — please retry');
    } finally {
      setBusy(false);
      // Reset "done" state after 4s so the button is re-usable
      setTimeout(() => setDone(false), 4000);
    }
  };

  return (
    <>
      {/* Trigger button — rendered inline above the Trophy Cabinet */}
      <div className="text-center" data-testid="honors-snapshot-trigger-wrapper">
        <button
          onClick={handleShare}
          disabled={busy}
          data-testid="honors-snapshot-share-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 group"
          style={{
            background: busy
              ? 'rgba(15, 23, 42, 0.5)'
              : 'linear-gradient(135deg, rgba(251, 191, 36, 0.18) 0%, rgba(251, 191, 36, 0.08) 100%)',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            color: '#fbbf24',
            boxShadow: busy ? 'none' : '0 6px 22px rgba(251, 191, 36, 0.16)',
          }}
        >
          {busy ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Composing snapshot…
            </>
          ) : done ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              Snapshot ready
            </>
          ) : (
            <>
              <Share2 className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
              Share Honors Snapshot
              <span className="hidden sm:inline opacity-60 text-[10px] ml-1">
                · 1200×630 PNG
              </span>
            </>
          )}
        </button>
        <p className="text-[10px] text-slate-500 mt-2">
          One tap shares to Instagram · WhatsApp · LinkedIn (mobile) · PNG download (desktop)
        </p>
      </div>

      {/* Off-screen 1200×630 blueprint that html2canvas captures */}
      <div
        ref={blueprintRef}
        data-testid="honors-snapshot-blueprint"
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: '1200px',
          height: '630px',
          // html2canvas can mis-handle Tailwind so we use plain inline styles
          background: '#020617',
          color: '#ffffff',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          padding: '48px',
          display: 'flex',
          gap: '36px',
          alignItems: 'stretch',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        {/* Subtle amber bloom — solid radial gradient renders well in html2canvas */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 10% 100%, rgba(251,191,36,0.10) 0%, transparent 55%)',
            pointerEvents: 'none',
          }}
        />

        {/* LEFT THIRD — Branding + Live Stats */}
        <div
          style={{
            width: '33%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            borderRight: '1px solid rgba(30, 41, 59, 0.9)',
            paddingRight: '32px',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div>
            <div
              style={{
                color: '#fbbf24',
                fontWeight: 800,
                fontSize: '12px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                marginBottom: '14px',
              }}
            >
              ✦ Verified Honors
            </div>
            <h2
              style={{
                fontSize: '32px',
                fontWeight: 900,
                lineHeight: 1.1,
                letterSpacing: '-0.01em',
                margin: 0,
                color: '#ffffff',
              }}
            >
              {academyName}
            </h2>
            {(academy?.city || academy?.state) && (
              <p
                style={{
                  fontSize: '13px',
                  color: '#94a3b8',
                  marginTop: '6px',
                  fontWeight: 600,
                }}
              >
                {[academy.city, academy.state].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <StatBlock label="Podium Finishes" value={stats?.podiums ?? 0} accent="#fbbf24" />
            <StatBlock label="Pro Pathway Signings" value={stats?.pro_promotions ?? 0} accent="#a5b4fc" />
            <StatBlock label="Win Velocity" value={stats?.win_rate ?? '—'} accent="#34d399" />
          </div>

          <div
            style={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#475569',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            out-play.in · Athlete Honors Network
          </div>
        </div>

        {/* RIGHT TWO-THIRDS — Top trophies podium */}
        <div
          style={{
            width: '67%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#fbbf24',
              marginBottom: '4px',
            }}
          >
            🏆 Top Accolades
          </div>

          {top3.length > 0 ? (
            top3.map((t, i) => (
              <TrophyRow key={i} trophy={t} index={i} />
            ))
          ) : (
            <div
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(30, 41, 59, 0.9)',
                borderRadius: '14px',
                padding: '28px',
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {(progressions[0]?.athlete_name && (
                <>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
                    {progressions[0].athlete_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#a5b4fc', marginTop: '6px', fontWeight: 700 }}>
                    → {progressions[0].destination_club}
                  </div>
                </>
              )) || 'Honors loading…'}
            </div>
          )}

          {/* Footer watermark */}
          <div
            style={{
              marginTop: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '11px',
              color: '#475569',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            <span>
              {stats?.athletes_tracked ?? 0} athletes tracked
            </span>
            <span style={{ color: '#fbbf24' }}>Powered by Out-Play</span>
          </div>
        </div>
      </div>
    </>
  );
}

function StatBlock({ label, value, accent }) {
  return (
    <div>
      <div
        style={{
          fontSize: '11px',
          color: '#64748b',
          fontWeight: 800,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          marginBottom: '4px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '40px',
          fontWeight: 900,
          color: accent,
          lineHeight: 1,
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function TrophyRow({ trophy, index }) {
  const accent = index === 0 ? '#fbbf24' : index === 1 ? '#94a3b8' : '#f97316';
  const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉';
  return (
    <div
      style={{
        background: 'rgba(15, 23, 42, 0.55)',
        border: '1px solid rgba(30, 41, 59, 0.95)',
        borderRadius: '14px',
        padding: '18px 22px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: '32px', lineHeight: 1, flexShrink: 0 }}>{medal}</div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: '17px',
              fontWeight: 800,
              color: '#ffffff',
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '440px',
            }}
          >
            {trophy.tournament_name}
          </div>
          <div
            style={{
              fontSize: '11px',
              color: accent,
              fontWeight: 700,
              marginTop: '4px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {trophy.division} · {trophy.rank}
          </div>
        </div>
      </div>
      {trophy.year && (
        <span
          style={{
            background: '#020617',
            border: '1px solid #1e293b',
            color: accent,
            fontSize: '15px',
            fontWeight: 900,
            padding: '6px 14px',
            borderRadius: '10px',
            letterSpacing: '0.05em',
            flexShrink: 0,
          }}
        >
          {trophy.year}
        </span>
      )}
    </div>
  );
}
